// AI PFMEA Ideation Wizard
// Generates PFMEA content using AI from process descriptions
// ============================================================

import React, { useState } from 'react';
import { useAppContext } from '../contexts/AppContext';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useTheme } from '../contexts/ThemeContext';
import {
    Sparkles,
    Zap,
    ArrowRight,
    Check,
    Activity,
    RefreshCw,
    Send,
    ChevronDown,
} from 'lucide-react';
import clsx from 'clsx';
import { authenticatedFetchJSON } from '../utils/authenticatedFetch';
import MethodologyTooltip from '../components/learn/MethodologyTooltip';

const IdeationPage: React.FC = () => {
    const navigate = useNavigate();
    const { standardConfig, activeProjectName, setProcessFlow, addPfmeaItem, activeStandard } = useAppContext();
    const { hasPermission } = useAuth();
    const { theme } = useTheme();
    const isDark = theme === 'dark';

    // Types for generated content
    interface GeneratedStep {
        id: string;
        stepNumber: number;
        name: string;
        description: string;
        category4M: string;
        equipment: string;
        failureModes: string[];
        isRefining?: boolean;
    }

    // State
    const [step, setStep] = useState(1);
    const [processDescription, setProcessDescription] = useState('');
    const [isGenerating, setIsGenerating] = useState(false);

    // NEW: Generated process steps state
    const [generatedSteps, setGeneratedSteps] = useState<GeneratedStep[]>([]);
    const [selectedStepId, setSelectedStepId] = useState<string | null>(null);
    const [refinementInput, setRefinementInput] = useState('');
    const [isRefining, setIsRefining] = useState(false);

    const steps = [
        { num: 1, title: 'Describe Process', icon: '📝' },
        { num: 2, title: 'Generate Steps', icon: '🔄' },
        { num: 3, title: 'Generate Failures', icon: '⚠️' },
        { num: 4, title: '4M Analysis', icon: '🔧' },
        { num: 5, title: 'Review & Save', icon: '✅' },
    ];



    const handleFinish = () => {
        // 1. Convert Generated Steps to Process Flow Diagram
        const newProcessSteps = generatedSteps.map((step, idx) => ({
            id: step.id,
            stepNumber: `OP-${(step.stepNumber * 10).toString().padStart(3, '0')}`,
            stepName: step.name,
            stepType: 'operation' as const,
            description: step.description,
            equipment: step.equipment,
            processElement: step.category4M as any, // Cast to CauseCategory
            workElementIds: [],
            linkedPfmeaIds: [],
            position: { x: 100, y: (idx + 1) * 150 } // Basic layout
        }));

        setProcessFlow({
            id: `flow-${Date.now()}`,
            projectName: activeProjectName || 'New Project',
            title: 'AI Generated Process Flow',
            revision: 'A',
            steps: newProcessSteps,
            status: 'draft'
        });

        // 2. Create PFMEA Items from Failure Modes
        generatedSteps.forEach(step => {
            step.failureModes.forEach(failureMode => {
                addPfmeaItem({
                    id: `pfmea-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
                    projectName: activeProjectName || 'New Project',
                    standard: activeStandard,
                    processStepNumber: `OP-${(step.stepNumber * 10).toString().padStart(3, '0')}`,
                    processStepName: step.name,
                    processElement: step.category4M as any,
                    processFunction: `Perform ${step.name} correctly`, // Inferred function
                    failureMode: failureMode,
                    failureEffectEnd: 'Process interruption / quality defect', // Placeholder
                    failureCause: 'To be analyzed', // Placeholder
                    severity: 0,
                    occurrence: 0,
                    detection: 0,
                    preventionControls: [],
                    detectionControls: [],
                    recommendedActions: [],
                    status: 'draft',
                    createdBy: 'AI Assistant',
                    createdAt: new Date(),
                    updatedAt: new Date()
                });
            });
        });

        // 3. Navigate to Editor
        navigate('/pfmea-editor');
    };

    const handleGenerate = async () => {
        if (!processDescription.trim()) return;
        setIsGenerating(true);

        try {
            const result = await authenticatedFetchJSON<any>('/api/agents/workflow/coach', {
                method: 'POST',
                body: JSON.stringify({
                    prompt: `You are an expert PFMEA engineer. Analyze this process and generate structured process steps.

Process Description: ${processDescription}
Industry: Manufacturing

Generate 5-10 process steps in JSON format. For each step provide:
{
  "steps": [
    {
      "stepNumber": 1,
      "name": "Step Name",
      "description": "Detailed description of what happens in this step",
      "category4M": "Machine|Man|Material|Method",
      "equipment": "Equipment used (if applicable)",
      "failureModes": ["Potential failure 1", "Potential failure 2"]
    }
  ]
}

Respond with ONLY the JSON, no other text.`
                }),
            });

            console.log('AI Generation result:', result);

            let stepsData: GeneratedStep[] = [];

            // Try to parse the response
            try {
                const responseText = typeof result === 'string' ? result :
                    result.response || result.content || JSON.stringify(result);

                // Extract JSON from response
                const jsonMatch = responseText.match(/\{[\s\S]*"steps"[\s\S]*\}/);
                if (jsonMatch) {
                    const parsed = JSON.parse(jsonMatch[0]);
                    stepsData = (parsed.steps || []).map((s: any, idx: number) => ({
                        id: `step-${Date.now()}-${idx}`,
                        stepNumber: s.stepNumber || idx + 1,
                        name: s.name || `Step ${idx + 1}`,
                        description: s.description || '',
                        category4M: s.category4M || 'Method',
                        equipment: s.equipment || '',
                        failureModes: s.failureModes || [],
                    }));
                }
            } catch (parseError) {
                console.warn('Could not parse AI response, using demo data', parseError);
            }

            // If no steps were parsed, generate demo steps based on description
            if (stepsData.length === 0) {
                const demoSteps = processDescription.toLowerCase().includes('weld')
                    ? [
                        { name: 'Material Preparation', description: 'Clean and prepare base materials for welding', category4M: 'Material', equipment: 'Cleaning station', failureModes: ['Contamination', 'Surface oxidation'] },
                        { name: 'Fixture Setup', description: 'Position and clamp workpieces in welding fixture', category4M: 'Machine', equipment: 'Welding fixture', failureModes: ['Misalignment', 'Loose clamp'] },
                        { name: 'Welding Parameters Setup', description: 'Set welding temperature, current, and speed parameters', category4M: 'Method', equipment: 'Welding machine', failureModes: ['Incorrect parameters', 'Equipment drift'] },
                        { name: 'Welding Operation', description: 'Execute welding process according to specifications', category4M: 'Man', equipment: 'Welding torch/robot', failureModes: ['Porosity', 'Undercut', 'Spatter', 'Cold crack'] },
                        { name: 'Post-Weld Inspection', description: 'Visual and NDT inspection of weld quality', category4M: 'Method', equipment: 'Inspection equipment', failureModes: ['Missed defect', 'False rejection'] },
                    ]
                    : [
                        { name: 'Raw Material Receipt', description: 'Receive and verify incoming materials', category4M: 'Material', equipment: 'Receiving dock', failureModes: ['Wrong material', 'Damaged shipment'] },
                        { name: 'Material Staging', description: 'Stage materials for production', category4M: 'Man', equipment: 'Staging area', failureModes: ['Wrong location', 'FIFO violation'] },
                        { name: 'Primary Processing', description: 'Main manufacturing operation', category4M: 'Machine', equipment: 'Production equipment', failureModes: ['Dimension out of spec', 'Surface defect'] },
                        { name: 'Secondary Processing', description: 'Additional processing or finishing', category4M: 'Method', equipment: 'Secondary equipment', failureModes: ['Process variation', 'Over-processing'] },
                        { name: 'Final Inspection', description: 'Verify product meets all specifications', category4M: 'Method', equipment: 'CMM/Inspection', failureModes: ['Escape defect', 'Type I error'] },
                    ];

                stepsData = demoSteps.map((s, idx) => ({
                    id: `step-${Date.now()}-${idx}`,
                    stepNumber: idx + 1,
                    ...s,
                }));
            }

            setGeneratedSteps(stepsData);
            setStep(2);
        } catch (error) {
            console.error('Generation error:', error);
            // Fallback with demo data
            setGeneratedSteps([
                { id: 'step-1', stepNumber: 1, name: 'Process Step 1', description: 'First step of the process', category4M: 'Method', equipment: 'Equipment A', failureModes: ['Failure mode 1'] },
                { id: 'step-2', stepNumber: 2, name: 'Process Step 2', description: 'Second step of the process', category4M: 'Machine', equipment: 'Equipment B', failureModes: ['Failure mode 2'] },
                { id: 'step-3', stepNumber: 3, name: 'Process Step 3', description: 'Third step of the process', category4M: 'Man', equipment: 'Workstation', failureModes: ['Failure mode 3'] },
            ]);
            setStep(2);
        } finally {
            setIsGenerating(false);
        }
    };

    // NEW: Handle refinement of a specific step
    const handleRefineStep = async (stepId: string) => {
        if (!refinementInput.trim()) return;

        const stepToRefine = generatedSteps.find(s => s.id === stepId);
        if (!stepToRefine) return;

        setIsRefining(true);

        try {
            const result = await authenticatedFetchJSON<any>('/api/agents/workflow/coach', {
                method: 'POST',
                body: JSON.stringify({
                    prompt: `You are an expert PFMEA engineer. I need you to refine a specific process step.

Current Step:
- Name: ${stepToRefine.name}
- Description: ${stepToRefine.description}
- 4M Category: ${stepToRefine.category4M}
- Equipment: ${stepToRefine.equipment}
- Failure Modes: ${stepToRefine.failureModes.join(', ')}

User's Refinement Request: ${refinementInput}

Provide the refined step in JSON format:
{
  "name": "Updated step name",
  "description": "Updated detailed description",
  "category4M": "Machine|Man|Material|Method",
  "equipment": "Updated equipment",
  "failureModes": ["Updated failure mode 1", "Updated failure mode 2", ...]
}

Respond with ONLY the JSON.`
                }),
            });

            let updatedStep: Partial<GeneratedStep> = {};

            try {
                const responseText = typeof result === 'string' ? result :
                    result.response || result.content || JSON.stringify(result);
                const jsonMatch = responseText.match(/\{[\s\S]*\}/);
                if (jsonMatch) {
                    updatedStep = JSON.parse(jsonMatch[0]);
                }
            } catch (parseError) {
                console.warn('Could not parse refinement response', parseError);
            }

            // Apply refinement (even partial updates)
            if (Object.keys(updatedStep).length > 0 || refinementInput) {
                setGeneratedSteps(steps => steps.map(s =>
                    s.id === stepId
                        ? {
                            ...s,
                            name: updatedStep.name || s.name,
                            description: updatedStep.description ||
                                (refinementInput.toLowerCase().includes('more detail')
                                    ? s.description + ' [Enhanced with: ' + refinementInput + ']'
                                    : s.description),
                            category4M: updatedStep.category4M || s.category4M,
                            equipment: updatedStep.equipment || s.equipment,
                            failureModes: updatedStep.failureModes || s.failureModes,
                        }
                        : s
                ));
            }

            // Clear refinement UI
            setSelectedStepId(null);
            setRefinementInput('');
        } catch (error) {
            console.error('Refinement error:', error);
            // Still apply the user's request as a note
            setGeneratedSteps(steps => steps.map(s =>
                s.id === stepId
                    ? { ...s, description: s.description + '\n\n[Refinement requested: ' + refinementInput + ']' }
                    : s
            ));
            setSelectedStepId(null);
            setRefinementInput('');
        } finally {
            setIsRefining(false);
        }
    };



    if (!hasPermission('ai.generate_pfmea')) {
        return (
            <div className="min-h-[60vh] flex items-center justify-center">
                <div className="text-center">
                    <Sparkles className="mx-auto text-slate-400 mb-4" size={48} />
                    <h2 className={clsx('text-xl font-semibold mb-2', isDark ? 'text-white' : 'text-slate-800')}>
                        AI Ideation Not Available
                    </h2>
                    <p className="text-slate-500">
                        You need Engineer or higher role to use AI generation features.
                    </p>
                </div>
            </div>
        );
    }

    return (
        <div className="space-y-6">
            {/* Header */}
            <div className="flex items-center justify-between">
                <div>
                    <h1 className={clsx('text-2xl font-bold flex items-center gap-2', isDark ? 'text-white' : 'text-slate-800')}>
                        <Zap className="text-blue-600" />
                        AI PFMEA Ideation Wizard
                    </h1>
                    <p className="text-slate-500 text-sm">
                        Generate PFMEA content using AI from process descriptions ({standardConfig?.name || 'AIAG-VDA'})
                    </p>
                </div>
            </div>

            {/* Ideation Wizard */}
            <div>
                {/* Progress Steps */}
                <div className={clsx('rounded-xl border p-4', isDark ? 'bg-slate-800 border-slate-700' : 'bg-white border-slate-200')}>
                    <div className="flex items-center justify-between">
                        {steps.map((s, idx) => (
                            <React.Fragment key={s.num}>
                                <div className={`flex flex-col items-center ${step >= s.num ? 'opacity-100' : 'opacity-40'}`}>
                                    <div className={clsx(
                                        'w-12 h-12 rounded-full flex items-center justify-center text-xl',
                                        step > s.num ? 'bg-green-500 text-white' :
                                            step === s.num ? 'bg-blue-600 text-white' :
                                                isDark ? 'bg-slate-700' : 'bg-slate-100'
                                    )}>
                                        {step > s.num ? <Check size={24} /> : s.icon}
                                    </div>
                                    <p className="text-xs mt-2 text-center text-slate-500">{s.title}</p>
                                </div>
                                {idx < steps.length - 1 && (
                                    <div className={clsx('flex-1 h-1 mx-2 rounded', step > s.num ? 'bg-green-500' : isDark ? 'bg-slate-700' : 'bg-slate-200')} />
                                )}
                            </React.Fragment>
                        ))}
                    </div>
                </div>

                {/* Step Content */}
                <div className={clsx('rounded-xl border p-6', isDark ? 'bg-slate-800 border-slate-700' : 'bg-white border-slate-200')}>
                    {step === 1 && (
                        <div className="space-y-6">
                            <div>
                                <h2 className={clsx('text-lg font-semibold mb-2 flex items-center gap-2', isDark ? 'text-white' : 'text-slate-800')}>
                                    Step 1: Describe Your Manufacturing Process
                                    <MethodologyTooltip lessonId="pfmea-intro" />
                                </h2>
                                <p className="text-slate-500 text-sm">
                                    Provide a detailed description of the manufacturing process. The AI will identify process steps and potential failure modes.
                                </p>
                            </div>

                            <textarea
                                value={processDescription}
                                onChange={(e) => setProcessDescription(e.target.value)}
                                placeholder="Example: Heat treatment of automotive transmission gears includes loading into baskets, preheating, carburizing, quenching in oil, tempering, and final inspection..."
                                className={clsx(
                                    'w-full h-48 p-4 rounded-xl border focus:outline-none focus:ring-2 focus:ring-blue-600 resize-none',
                                    isDark ? 'bg-slate-700 border-slate-600 text-white placeholder-slate-400' : 'bg-slate-50 border-slate-200 text-slate-800'
                                )}
                            />

                            <div className="flex items-center justify-between">
                                <p className="text-sm text-slate-500">
                                    Standard: <span className={clsx('font-medium', isDark ? 'text-white' : 'text-slate-800')}>{standardConfig?.name || 'AIAG-VDA'}</span>
                                </p>
                                <button
                                    onClick={handleGenerate}
                                    disabled={!processDescription.trim() || isGenerating}
                                    className="px-6 py-3 bg-gradient-to-r from-blue-600 to-pink-500 text-white rounded-xl font-semibold hover:opacity-90 transition-opacity disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
                                >
                                    {isGenerating ? (
                                        <>
                                            <RefreshCw size={18} className="animate-spin" />
                                            Generating...
                                        </>
                                    ) : (
                                        <>
                                            <Zap size={18} />
                                            Generate Process Steps
                                            <ArrowRight size={18} />
                                        </>
                                    )}
                                </button>
                            </div>
                        </div>
                    )}

                    {step >= 2 && (
                        <div className="space-y-6">
                            {/* Header with back button */}
                            <div className="flex justify-between items-center">
                                <div>
                                    <h2 className={clsx('text-lg font-semibold', isDark ? 'text-white' : 'text-slate-800')}>
                                        Step 2: Review & Refine Process Steps
                                    </h2>
                                    <p className="text-slate-500 text-sm">
                                        Click on any step to refine it. Say things like "add more detail about welding temp" or "add another failure mode".
                                    </p>
                                </div>
                                <button
                                    onClick={() => setStep(1)}
                                    className="px-3 py-1.5 text-sm text-blue-600 hover:text-indigo-600"
                                >
                                    ← Back
                                </button>
                            </div>

                            {/* Generated Steps List */}
                            <div className="space-y-4">
                                {generatedSteps.map((genStep) => (
                                    <div
                                        key={genStep.id}
                                        className={clsx(
                                            'rounded-xl border p-4 transition-all cursor-pointer',
                                            selectedStepId === genStep.id
                                                ? 'ring-2 ring-blue-600 border-blue-300'
                                                : isDark ? 'border-slate-600 hover:border-slate-500' : 'border-slate-200 hover:border-slate-300',
                                            isDark ? 'bg-slate-700/50' : 'bg-slate-50'
                                        )}
                                        onClick={() => setSelectedStepId(selectedStepId === genStep.id ? null : genStep.id)}
                                    >
                                        {/* Step Header */}
                                        <div className="flex items-start justify-between mb-3">
                                            <div className="flex items-center gap-3">
                                                <div className={clsx(
                                                    'w-10 h-10 rounded-lg flex items-center justify-center text-lg font-bold',
                                                    genStep.category4M === 'Machine' ? 'bg-blue-100 text-blue-700 dark:bg-blue-900/50 dark:text-blue-300' :
                                                        genStep.category4M === 'Man' ? 'bg-green-100 text-green-700 dark:bg-green-900/50 dark:text-green-300' :
                                                            genStep.category4M === 'Material' ? 'bg-orange-100 text-orange-700 dark:bg-orange-900/50 dark:text-orange-300' :
                                                                'bg-blue-100 text-indigo-700 dark:bg-purple-900/50 dark:text-blue-300'
                                                )}>
                                                    {genStep.stepNumber}
                                                </div>
                                                <div>
                                                    <h3 className={clsx('font-semibold', isDark ? 'text-white' : 'text-slate-800')}>
                                                        {genStep.name}
                                                    </h3>
                                                    <span className={clsx(
                                                        'text-xs px-2 py-0.5 rounded-full flex items-center gap-1',
                                                        genStep.category4M === 'Machine' ? 'bg-blue-100 text-blue-700 dark:bg-blue-900/50 dark:text-blue-300' :
                                                            genStep.category4M === 'Man' ? 'bg-green-100 text-green-700 dark:bg-green-900/50 dark:text-green-300' :
                                                                genStep.category4M === 'Material' ? 'bg-orange-100 text-orange-700 dark:bg-orange-900/50 dark:text-orange-300' :
                                                                    'bg-blue-100 text-indigo-700 dark:bg-purple-900/50 dark:text-blue-300'
                                                    )}>
                                                        {genStep.category4M}
                                                        <MethodologyTooltip lessonId="risk-analysis" />
                                                    </span>
                                                </div>
                                            </div>
                                            <ChevronDown
                                                size={20}
                                                className={clsx(
                                                    'text-slate-400 transition-transform',
                                                    selectedStepId === genStep.id && 'rotate-180'
                                                )}
                                            />
                                        </div>

                                        {/* Step Details (always visible) */}
                                        <p className="text-sm text-slate-500 mb-2">{genStep.description}</p>

                                        {genStep.equipment && (
                                            <p className="text-xs text-slate-400">
                                                <span className="font-medium">Equipment:</span> {genStep.equipment}
                                            </p>
                                        )}

                                        {/* Failure Modes */}
                                        {genStep.failureModes.length > 0 && (
                                            <div className="mt-3 flex flex-wrap gap-2">
                                                {genStep.failureModes.map((fm, i) => (
                                                    <span
                                                        key={i}
                                                        className={clsx(
                                                            'text-xs px-2 py-1 rounded',
                                                            isDark ? 'bg-red-900/30 text-red-300' : 'bg-red-50 text-red-700'
                                                        )}
                                                    >
                                                        ⚠️ {fm}
                                                    </span>
                                                ))}
                                            </div>
                                        )}

                                        {/* Refinement Input (when selected) */}
                                        {selectedStepId === genStep.id && (
                                            <div className="mt-4 pt-4 border-t border-slate-200 dark:border-slate-600" onClick={(e) => e.stopPropagation()}>
                                                <p className="text-sm font-medium mb-2 text-indigo-600 dark:text-blue-400">
                                                    ✨ Refine this step
                                                </p>
                                                <div className="flex gap-2">
                                                    <input
                                                        type="text"
                                                        value={refinementInput}
                                                        onChange={(e) => setRefinementInput(e.target.value)}
                                                        onKeyPress={(e) => e.key === 'Enter' && handleRefineStep(genStep.id)}
                                                        placeholder="e.g., 'Add more detail about welding temperature range'"
                                                        className={clsx(
                                                            'flex-1 px-3 py-2 rounded-lg border text-sm focus:outline-none focus:ring-2 focus:ring-blue-600',
                                                            isDark ? 'bg-slate-600 border-slate-500 text-white placeholder-slate-400' : 'bg-white border-slate-300'
                                                        )}
                                                    />
                                                    <button
                                                        onClick={() => handleRefineStep(genStep.id)}
                                                        disabled={!refinementInput.trim() || isRefining}
                                                        className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-indigo-600 disabled:opacity-50 text-sm flex items-center gap-2"
                                                    >
                                                        {isRefining ? (
                                                            <RefreshCw size={14} className="animate-spin" />
                                                        ) : (
                                                            <Send size={14} />
                                                        )}
                                                        Refine
                                                    </button>
                                                </div>
                                                <p className="text-xs text-slate-400 mt-2">
                                                    Examples: "add thermal shock failure mode", "specify tolerance ±0.01mm", "include operator training requirement"
                                                </p>
                                            </div>
                                        )}
                                    </div>
                                ))}
                            </div>

                            {/* Action Buttons */}
                            <div className="flex justify-between pt-4 border-t border-slate-200 dark:border-slate-700">
                                <button
                                    onClick={() => {
                                        setGeneratedSteps([]);
                                        setStep(1);
                                    }}
                                    className="px-4 py-2 text-slate-500 hover:text-slate-700"
                                >
                                    Start Over
                                </button>
                                <div className="flex gap-3">
                                    <button
                                        onClick={handleFinish}
                                        className="px-6 py-2 bg-gradient-to-r from-blue-600 to-pink-500 text-white rounded-lg font-medium hover:opacity-90 flex items-center gap-2"
                                    >
                                        Finish & Open Editor
                                        <ArrowRight size={16} />
                                    </button>
                                </div>
                            </div>
                        </div>
                    )}
                </div>

                {/* AIAG-VDA Workflow Benefits Info Card */}
                <div className={clsx('p-5 rounded-xl border', isDark ? 'bg-gradient-to-br from-blue-900/30 to-indigo-900/30 border-blue-800' : 'bg-gradient-to-br from-blue-50 to-indigo-50 border-blue-200')}>
                    <div className="flex items-start gap-4">
                        <div className={clsx('w-12 h-12 rounded-xl flex items-center justify-center flex-shrink-0', isDark ? 'bg-blue-600' : 'bg-blue-500')}>
                            <Sparkles className="text-white" size={24} />
                        </div>
                        <div className="flex-1">
                            <h3 className={clsx('font-bold text-lg mb-2', isDark ? 'text-white' : 'text-slate-800')}>
                                🚀 How This Integrated Flow Helps You
                            </h3>
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-3">
                                <div className={clsx('p-3 rounded-lg', isDark ? 'bg-slate-800/50' : 'bg-white/70')}>
                                    <div className="flex items-center gap-2 mb-1">
                                        <span className="text-lg">🚫</span>
                                        <span className={clsx('font-semibold text-sm', isDark ? 'text-green-400' : 'text-green-700')}>Solves Blank Page Problem</span>
                                    </div>
                                    <p className={clsx('text-xs', isDark ? 'text-slate-400' : 'text-slate-600')}>
                                        Never stare at an empty worksheet. AI generates process steps and failure modes from your description.
                                    </p>
                                </div>
                                <div className={clsx('p-3 rounded-lg', isDark ? 'bg-slate-800/50' : 'bg-white/70')}>
                                    <div className="flex items-center gap-2 mb-1">
                                        <span className="text-lg">⏱️</span>
                                        <span className={clsx('font-semibold text-sm', isDark ? 'text-blue-400' : 'text-blue-700')}>Saves Hours of Work</span>
                                    </div>
                                    <p className={clsx('text-xs', isDark ? 'text-slate-400' : 'text-slate-600')}>
                                        Auto-generates 4M categories, failure modes, and links to Control Plan. Refine with natural language.
                                    </p>
                                </div>
                                <div className={clsx('p-3 rounded-lg', isDark ? 'bg-slate-800/50' : 'bg-white/70')}>
                                    <div className="flex items-center gap-2 mb-1">
                                        <span className="text-lg">✅</span>
                                        <span className={clsx('font-semibold text-sm', isDark ? 'text-purple-400' : 'text-purple-700')}>AIAG-VDA Compliant</span>
                                    </div>
                                    <p className={clsx('text-xs', isDark ? 'text-slate-400' : 'text-slate-600')}>
                                        Follows the 7-step methodology. Menu guides you through: Planning → Structure → Function → Failure → Risk → Optimize → Document.
                                    </p>
                                </div>
                                <div className={clsx('p-3 rounded-lg', isDark ? 'bg-slate-800/50' : 'bg-white/70')}>
                                    <div className="flex items-center gap-2 mb-1">
                                        <span className="text-lg">👥</span>
                                        <span className={clsx('font-semibold text-sm', isDark ? 'text-amber-400' : 'text-amber-700')}>Team Onboarding</span>
                                    </div>
                                    <p className={clsx('text-xs', isDark ? 'text-slate-400' : 'text-slate-600')}>
                                        New engineers can follow the workflow immediately. Reduces training time and ensures consistency.
                                    </p>
                                </div>
                            </div>
                            <div className={clsx('text-xs p-2 rounded-lg flex items-center gap-2', isDark ? 'bg-slate-700/50 text-slate-300' : 'bg-white text-slate-600')}>
                                <Activity size={14} className="text-primary-500" />
                                <span>
                                    <strong>Workflow:</strong> Step 1 (Planning) → Step 2 (Process Flow) → Step 3 (Function Matrix) → Step 4 (PFMEA Worksheet) → Step 5 (Risk) → Step 6 (Actions) → Step 7 (Control Plan)
                                </span>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Tips */}
                <div className={clsx('p-4 rounded-xl border', isDark ? 'bg-purple-900/20 border-blue-800' : 'bg-purple-50 border-blue-200')}>
                    <h3 className="font-semibold text-indigo-700 dark:text-blue-300 mb-2">💡 Tips for Better Results</h3>
                    <ul className="text-sm text-indigo-600 dark:text-blue-400 space-y-1">
                        <li>• Be specific about equipment, materials, and process parameters</li>
                        <li>• Mention any special characteristics or critical quality requirements</li>
                        <li>• Include information about operators and environmental conditions</li>
                        <li>• Reference industry-specific standards if applicable</li>
                    </ul>
                </div>
            </div>
        </div >
    );
};

export default IdeationPage;
